import { useEffect, useRef } from "react";

interface AetherBackdropProps {
  accent: string;
  accentSoft: string;
  lineage: string;
}

interface Rgb {
  r: number;
  g: number;
  b: number;
}

interface Particle {
  x: number;
  y: number;
  drift: number;
  speed: number;
  size: number;
  phase: number;
}

const LINEAGE_SEEDS: Record<string, number> = {
  red: 0.11,
  blue: 0.24,
  green: 0.37,
  black: 0.43,
  white: 0.52,
  gold: 0.67,
  silver: 0.76,
  bronze: 0.83,
  brass: 0.91,
  copper: 0.98
};

function hexToRgb(value: string): Rgb {
  const normalized = value.replace("#", "").trim();
  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((char) => `${char}${char}`)
          .join("")
      : normalized;
  const parsed = Number.parseInt(expanded.slice(0, 6), 16);
  if (Number.isNaN(parsed)) return { r: 201, g: 164, b: 85 };
  return {
    r: (parsed >> 16) & 255,
    g: (parsed >> 8) & 255,
    b: parsed & 255
  };
}

function rgba(color: Rgb, alpha: number): string {
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
}

function seeded(index: number, seed: number): number {
  return Math.sin((index + 1) * 927.31 + seed * 1031.7) * 43758.5453 % 1;
}

export function AetherBackdrop({ accent, accentSoft, lineage }: AetherBackdropProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext("2d", { alpha: true });
    if (!context) return;

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    let width = 0;
    let height = 0;
    let animationFrame = 0;
    let particles: Particle[] = [];
    const accentRgb = hexToRgb(accent);
    const softRgb = hexToRgb(accentSoft);
    const lineageSeed = LINEAGE_SEEDS[lineage] ?? 0.5;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.75);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);

      const count = Math.round(Math.min(72, Math.max(28, width / 28)));
      particles = Array.from({ length: count }, (_, index) => {
        const seed = Math.abs(seeded(index, lineageSeed));
        const seedB = Math.abs(seeded(index + 17, lineageSeed));
        return {
          x: seed * width,
          y: seedB * height,
          drift: 0.18 + Math.abs(seeded(index + 33, lineageSeed)) * 0.78,
          speed: 0.08 + Math.abs(seeded(index + 51, lineageSeed)) * 0.24,
          size: 4 + Math.abs(seeded(index + 82, lineageSeed)) * 15,
          phase: Math.abs(seeded(index + 113, lineageSeed)) * Math.PI * 2
        };
      });
    };

    const draw = (time: number) => {
      const reducedMotion = motionQuery.matches;
      const t = reducedMotion ? 0 : time * 0.00018;
      context.clearRect(0, 0, width, height);
      context.globalCompositeOperation = "source-over";

      const veil = context.createLinearGradient(0, 0, width, height);
      veil.addColorStop(0, "rgba(8, 9, 10, 0.02)");
      veil.addColorStop(0.45, rgba(accentRgb, 0.045));
      veil.addColorStop(1, "rgba(52, 17, 27, 0.08)");
      context.fillStyle = veil;
      context.fillRect(0, 0, width, height);

      context.globalCompositeOperation = "screen";
      for (let lane = 0; lane < 8; lane += 1) {
        const laneSeed = lineageSeed * 12 + lane;
        const yBase = height * (0.08 + lane * 0.118);
        context.beginPath();
        for (let step = 0; step <= 72; step += 1) {
          const x = (step / 72) * width;
          const wave =
            Math.sin(step * 0.34 + t * (1.2 + lane * 0.12) + laneSeed) * 24 +
            Math.sin(step * 0.13 - t * 2 + laneSeed * 1.8) * 18;
          const y = yBase + wave;
          if (step === 0) context.moveTo(x, y);
          else context.lineTo(x, y);
        }
        context.strokeStyle = lane % 2 ? rgba(softRgb, 0.05) : rgba(accentRgb, 0.07);
        context.lineWidth = lane % 3 === 0 ? 1.25 : 0.7;
        context.stroke();
      }

      for (const particle of particles) {
        const y = (particle.y + t * 210 * particle.speed) % (height + 40);
        const x = particle.x + Math.sin(t * particle.drift + particle.phase) * 28;
        context.save();
        context.translate(x, y);
        context.rotate(particle.phase + t * 0.9);
        context.strokeStyle = rgba(particle.size > 12 ? softRgb : accentRgb, 0.14);
        context.lineWidth = particle.size > 11 ? 1 : 0.65;
        context.beginPath();
        context.moveTo(-particle.size * 0.5, 0);
        context.lineTo(particle.size * 0.5, 0);
        context.moveTo(0, -particle.size * 0.17);
        context.lineTo(0, particle.size * 0.17);
        context.stroke();
        context.restore();
      }

      if (!reducedMotion) {
        animationFrame = window.requestAnimationFrame(draw);
      }
    };

    resize();
    draw(0);
    window.addEventListener("resize", resize);
    motionQuery.addEventListener("change", resize);
    if (!motionQuery.matches) {
      animationFrame = window.requestAnimationFrame(draw);
    }

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", resize);
      motionQuery.removeEventListener("change", resize);
    };
  }, [accent, accentSoft, lineage]);

  return <canvas ref={canvasRef} className="aether-backdrop" aria-hidden="true" />;
}
